import { useEffect } from 'react';
import {
  useSpring,
  type SpringRef,
  type ControllerUpdate,
  type Lookup,
  type SpringValues,
} from 'react-spring';

/**
 * When using `useSpring`, React Strict Mode’s extra mount cycles
 * can cause animations to fail to properly initialize.
 *
 * This wrapper around `useSpring` ensures the animation is properly
 * initialized and cleaned up, despite the extra mount cycles.
 *
 * Fix: Sets the `{ to, immediate }` properties via a `useEffect`
 * so that they are synchronized with the component's lifecycle.
 *
 * @see {@link https://github.com/vercel/next.js/discussions/68141 "Issues with `@react-spring/web` and React 18 in strict mode"}
 * @see {@link https://gsap.com/community/forums/topic/31712-simple-opacity-fade-doesnt-work-in-react/ "Simple opacity fade doesn't work in React?" (`gsap`)}
 */
export function useMentiSpring<T extends object = any>(
  props: ControllerUpdate<T, undefined> & Partial<T> & { immediate?: boolean },
): [SpringValues<T>, SpringRef<Lookup<any>>] {
  const { to, immediate = false, ...initialProps } = props;

  const [springValue, springRef] = useSpring<T>(() => initialProps) as [
    SpringValues<T>,
    SpringRef<Lookup<any>>,
  ];

  useEffect(() => {
    springRef.start({
      to,
      immediate,
    });

    return () => {
      /**
       * On cleanup, cancels any pending animations.
       *
       * By stopping the "previous" animation before starting a "new" one,
       * we can potentially avoid issues where the animations overlap if
       * the dependencies change rapidly.
       */
      springRef.stop();
    };
  }, [springRef, to, immediate]);

  return [springValue, springRef];
}
